/**
 * Typed GA4 funnel contract. This is the only browser module allowed to call
 * gtag. Event payloads are allowlisted and validated again at runtime.
 */
import { sanitiseAnalyticsPath } from '@/public-route-manifest';
import { COUNCIL_REPORT_PAGE_SLUGS } from '@fixtyer/content';

export const CONSENT_STORAGE_KEY = 'fixtyer_cookie_consent';
export const JOURNEY_STORAGE_KEY = 'fixtyer_journey_context';
export const FUNNEL_VARIANT = 'guest_check' as const;

export type SourcePageType =
  | 'council'
  | 'guide'
  | 'blog'
  | 'home'
  | 'pricing'
  | 'press'
  | 'lp'
  | 'other';
export type PackType = 'council' | 'court';
export type WizardStep = 'customer' | 'incident' | 'location' | 'damage' | 'vehicle';
export type EligibilityStep = 'incident' | 'location';
export type EligibilityResult = 'authority_likely' | 'authority_uncertain';
export type RatingBand = 'green' | 'amber' | 'red' | 'not_scored';
export type CtaDestination = '/register' | '/dashboard' | '/claims/new' | '/check';
export type CtaId =
  | 'primary_conversion'
  | 'council_conversion'
  | 'guide_conversion'
  | 'eligibility_check'
  | 'authenticated_dashboard';
export type PaymentErrorCode =
  | 'stripe_load_failed'
  | 'payment_intent_failed'
  | 'payment_element_submit_failed'
  | 'payment_confirmation_failed'
  | 'backend_confirmation_failed'
  | 'missing_payment_intent';

interface JourneyContext {
  journey_id: string;
  source_path: string;
  source_page_type: SourcePageType;
  source_council_slug?: string;
  funnel_variant: typeof FUNNEL_VARIANT;
}

interface EventPayloads {
  page_view: Record<string, never>;
  cta_clicked: {
    cta_id: CtaId;
    cta_kind: 'primary' | 'guide' | 'council';
    destination: CtaDestination;
  };
  claim_wizard_started: Record<string, never>;
  claim_wizard_step_completed: { step: WizardStep };
  eligibility_started: Record<string, never>;
  eligibility_step_completed: { step: EligibilityStep };
  eligibility_result_viewed: {
    result: EligibilityResult;
    authority_slug?: string;
    rating_band?: RatingBand;
  };
  account_save_requested: Record<string, never>;
  sign_up: { method: 'email' };
  login: { method: 'email' };
  claim_created: Record<string, never>;
  evidence_uploaded: { evidence_type: 'auto' };
  pack_value_preview_viewed: { score_band: '0_39' | '40_69' | '70_89' | '90_100' };
  begin_checkout: { pack_type: PackType; value: number };
  purchase: { pack_type: PackType; value: number; transaction_id: string };
  payment_failed: { pack_type: PackType; error_code: PaymentErrorCode };
  pack_generated: { pack_type: PackType };
  pack_viewed: { pack_type: PackType };
}

export type FunnelEventName = keyof EventPayloads;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const PAGE_TYPES = new Set<SourcePageType>([
  'council',
  'guide',
  'blog',
  'home',
  'pricing',
  'press',
  'lp',
  'other',
]);
const PACK_TYPES = new Set<PackType>(['council', 'court']);
const WIZARD_STEPS = new Set<WizardStep>([
  'customer',
  'incident',
  'location',
  'damage',
  'vehicle',
]);
const PAYMENT_ERROR_CODES = new Set<PaymentErrorCode>([
  'stripe_load_failed',
  'payment_intent_failed',
  'payment_element_submit_failed',
  'payment_confirmation_failed',
  'backend_confirmation_failed',
  'missing_payment_intent',
]);
const CTA_IDS = new Set<CtaId>([
  'primary_conversion',
  'council_conversion',
  'guide_conversion',
  'eligibility_check',
  'authenticated_dashboard',
]);
const CTA_DESTINATIONS = new Set<CtaDestination>([
  '/register',
  '/dashboard',
  '/claims/new',
  '/check',
]);
const ELIGIBILITY_STEPS = new Set<EligibilityStep>(['incident', 'location']);
const ELIGIBILITY_RESULTS = new Set<EligibilityResult>([
  'authority_likely',
  'authority_uncertain',
]);
const RATING_BANDS = new Set<RatingBand>(['green', 'amber', 'red', 'not_scored']);
const COUNCIL_SLUGS = new Set(COUNCIL_REPORT_PAGE_SLUGS);

let lastTrackedRawPathBucket: string | null = null;
let inMemoryJourneyContext: JourneyContext | null = null;
const inMemoryDedupeKeys = new Set<string>();
let analyticsConsentInMemory: boolean | null = null;

function normalisePath(path: string): string {
  return sanitiseAnalyticsPath(path);
}

export function deriveSourceContext(path: string): Omit<JourneyContext, 'journey_id' | 'funnel_variant'> {
  const source_path = normalisePath(path);
  const councilMatch = source_path.match(/^\/councils\/([a-z0-9-]+)$/);
  const lpMatch = source_path.match(/^\/lp\/([a-z0-9-]+)$/);
  let source_page_type: SourcePageType = 'other';

  if (source_path === '/') source_page_type = 'home';
  else if (source_path === '/pricing') source_page_type = 'pricing';
  else if (councilMatch) source_page_type = 'council';
  else if (source_path === '/guides' || source_path.startsWith('/guides/')) source_page_type = 'guide';
  else if (source_path === '/blog' || source_path.startsWith('/blog/')) source_page_type = 'blog';
  else if (
    source_path === '/press' ||
    source_path.startsWith('/press/') ||
    source_path === '/media-enquiries'
  ) source_page_type = 'press';
  else if (lpMatch) source_page_type = 'lp';

  return {
    source_path,
    source_page_type,
    ...((councilMatch?.[1] || lpMatch?.[1])
      ? { source_council_slug: councilMatch?.[1] || lpMatch?.[1] }
      : {}),
  };
}

function makeJourneyId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index++) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function reconstructJourneyContext(value: unknown): JourneyContext | null {
  if (!value || typeof value !== 'object') return null;
  const context = value as Record<string, unknown>;
  if (
    typeof context.journey_id !== 'string' ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
      .test(context.journey_id) ||
    typeof context.source_path !== 'string' ||
    normalisePath(context.source_path) !== context.source_path ||
    !PAGE_TYPES.has(context.source_page_type as SourcePageType) ||
    context.funnel_variant !== FUNNEL_VARIANT
  ) {
    return null;
  }

  const derived = deriveSourceContext(context.source_path);
  const storedCouncil =
    typeof context.source_council_slug === 'string' ? context.source_council_slug : undefined;
  if (
    derived.source_page_type !== context.source_page_type ||
    derived.source_council_slug !== storedCouncil
  ) {
    return null;
  }

  return {
    journey_id: context.journey_id,
    source_path: derived.source_path,
    source_page_type: derived.source_page_type,
    ...(derived.source_council_slug
      ? { source_council_slug: derived.source_council_slug }
      : {}),
    funnel_variant: FUNNEL_VARIANT,
  };
}

export function getJourneyContext(initialPath?: string): JourneyContext {
  if (hasAnalyticsConsent()) {
    try {
      const stored = sessionStorage.getItem(JOURNEY_STORAGE_KEY);
      if (stored) {
        const parsed: unknown = JSON.parse(stored);
        const reconstructed = reconstructJourneyContext(parsed);
        if (reconstructed) {
          inMemoryJourneyContext = reconstructed;
          return reconstructed;
        }
      }
    } catch {
      // A fresh in-memory context is still safe when session storage is blocked.
    }
  }

  if (inMemoryJourneyContext) return inMemoryJourneyContext;

  const context: JourneyContext = {
    journey_id: makeJourneyId(),
    ...deriveSourceContext(initialPath ?? window.location.pathname),
    funnel_variant: FUNNEL_VARIANT,
  };
  inMemoryJourneyContext = context;
  if (hasAnalyticsConsent()) persistJourneyContext(context);
  return context;
}

function hasAnalyticsConsent(): boolean {
  if (analyticsConsentInMemory !== null) return analyticsConsentInMemory;
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY) === 'analytics';
  } catch {
    return false;
  }
}

function persistJourneyContext(context = inMemoryJourneyContext): void {
  if (!context) return;
  try {
    sessionStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(context));
  } catch {
    // The context remains available in memory when browser storage is blocked.
  }
}

function clearAnalyticsSessionStorage(): void {
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index--) {
      const key = sessionStorage.key(index);
      if (key === JOURNEY_STORAGE_KEY || key?.startsWith('fixtyer_event_')) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // Consent still applies in memory when browser storage is blocked.
  }
  inMemoryDedupeKeys.clear();
  lastTrackedRawPathBucket = null;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const hasOnlyKeys = (payload: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(payload).every((key) => keys.includes(key));
const isSafeValue = (value: number) => Number.isFinite(value) && value >= 0 && value <= 10000;

function validatePayload<E extends FunnelEventName>(
  event: E,
  payload: unknown
): payload is EventPayloads[E] {
  if (!isPlainObject(payload)) return false;

  switch (event) {
    case 'page_view':
    case 'claim_wizard_started':
    case 'claim_created':
    case 'eligibility_started':
    case 'account_save_requested':
      return hasOnlyKeys(payload, []);
    case 'cta_clicked':
      return (
        hasOnlyKeys(payload, ['cta_id', 'cta_kind', 'destination']) &&
        CTA_IDS.has(payload.cta_id as CtaId) &&
        ['primary', 'guide', 'council'].includes(String(payload.cta_kind)) &&
        CTA_DESTINATIONS.has(payload.destination as CtaDestination)
      );
    case 'claim_wizard_step_completed':
      return (
        hasOnlyKeys(payload, ['step']) &&
        WIZARD_STEPS.has(payload.step as WizardStep)
      );
    case 'eligibility_step_completed':
      return (
        hasOnlyKeys(payload, ['step']) &&
        ELIGIBILITY_STEPS.has(payload.step as EligibilityStep)
      );
    case 'eligibility_result_viewed':
      return (
        hasOnlyKeys(payload, ['result', 'authority_slug', 'rating_band']) &&
        ELIGIBILITY_RESULTS.has(payload.result as EligibilityResult) &&
        (payload.authority_slug === undefined ||
          COUNCIL_SLUGS.has(payload.authority_slug as string)) &&
        (payload.rating_band === undefined ||
          RATING_BANDS.has(payload.rating_band as RatingBand))
      );
    case 'sign_up':
    case 'login':
      return hasOnlyKeys(payload, ['method']) && payload.method === 'email';
    case 'evidence_uploaded':
      return hasOnlyKeys(payload, ['evidence_type']) && payload.evidence_type === 'auto';
    case 'pack_value_preview_viewed':
      return (
        hasOnlyKeys(payload, ['score_band']) &&
        ['0_39', '40_69', '70_89', '90_100'].includes(String(payload.score_band))
      );
    case 'begin_checkout':
      return (
        hasOnlyKeys(payload, ['pack_type', 'value']) &&
        PACK_TYPES.has(payload.pack_type as PackType) &&
        typeof payload.value === 'number' &&
        isSafeValue(payload.value)
      );
    case 'purchase':
      return (
        hasOnlyKeys(payload, ['pack_type', 'value', 'transaction_id']) &&
        PACK_TYPES.has(payload.pack_type as PackType) &&
        typeof payload.value === 'number' &&
        isSafeValue(payload.value) &&
        typeof payload.transaction_id === 'string' &&
        /^pi_[a-zA-Z0-9]+$/.test(payload.transaction_id)
      );
    case 'payment_failed':
      return (
        hasOnlyKeys(payload, ['pack_type', 'error_code']) &&
        PACK_TYPES.has(payload.pack_type as PackType) &&
        PAYMENT_ERROR_CODES.has(payload.error_code as PaymentErrorCode)
      );
    case 'pack_generated':
    case 'pack_viewed':
      return (
        hasOnlyKeys(payload, ['pack_type']) &&
        PACK_TYPES.has(payload.pack_type as PackType)
      );
    default:
      return false;
  }
}

function commerceItem(packType: PackType, value: number) {
  return {
    item_id: packType === 'council' ? 'council_pack' : 'court_pack',
    item_name: packType === 'council' ? 'Council Claim Pack' : 'Court Claim Pack',
    price: value,
    quantity: 1,
  };
}

function wasSentInSession(key: string): boolean {
  const storageKey = `fixtyer_event_${key}`;
  try {
    return sessionStorage.getItem(storageKey) === '1' || inMemoryDedupeKeys.has(key);
  } catch {
    return inMemoryDedupeKeys.has(key);
  }
}

function markSentInSession(key: string): void {
  inMemoryDedupeKeys.add(key);
  try {
    sessionStorage.setItem(`fixtyer_event_${key}`, '1');
  } catch {
    // gtag and GA4 transaction IDs provide the final purchase dedupe fallback.
  }
}

function trackDeduplicated<E extends FunnelEventName>(
  key: string,
  event: E,
  payload: EventPayloads[E]
): boolean {
  if (wasSentInSession(key)) return false;
  const sent = trackEvent(event, payload);
  if (sent) markSentInSession(key);
  return sent;
}

function localScopeBucket(scope: string): string {
  // One-way, local-only bucket: raw claim/pack IDs never enter storage or GA4.
  let hash = 2166136261;
  for (let index = 0; index < scope.length; index++) {
    hash ^= scope.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function sendEvent<E extends FunnelEventName>(
  event: E,
  payload: EventPayloads[E],
  path = window.location.pathname
): boolean {
  if (!hasAnalyticsConsent() || typeof window.gtag !== 'function') return false;
  if (!validatePayload(event, payload)) return false;

  const current_path = normalisePath(path);
  const context = getJourneyContext(path);
  const commerce =
    event === 'begin_checkout' || event === 'purchase'
      ? {
          currency: 'GBP',
          items: [
            commerceItem(
              (payload as EventPayloads['begin_checkout']).pack_type,
              (payload as EventPayloads['begin_checkout']).value
            ),
          ],
        }
      : {};

  window.gtag('event', event, {
    ...context,
    current_path,
    ...payload,
    ...commerce,
  });
  return true;
}

export function trackEvent<E extends FunnelEventName>(
  event: E,
  payload: EventPayloads[E]
): boolean {
  return sendEvent(event, payload);
}

export function updateGoogleConsent(choice: 'analytics' | 'essential'): void {
  analyticsConsentInMemory = choice === 'analytics';
  window.gtag?.('consent', 'update', {
    analytics_storage: choice === 'analytics' ? 'granted' : 'denied',
    ad_storage: choice === 'analytics' ? 'granted' : 'denied',
    ad_user_data: choice === 'analytics' ? 'granted' : 'denied',
    ad_personalization: choice === 'analytics' ? 'granted' : 'denied',
  });
  if (choice === 'essential') {
    clearAnalyticsSessionStorage();
    return;
  }

  const context = getJourneyContext();
  persistJourneyContext(context);
  trackPageView(window.location.pathname);
}

export function trackPageView(path: string): void {
  const rawPathBucket = localScopeBucket(path.split(/[?#]/, 1)[0] || '/');
  const currentPath = normalisePath(path);
  if (lastTrackedRawPathBucket === rawPathBucket) return;
  if (sendEvent('page_view', {}, currentPath)) lastTrackedRawPathBucket = rawPathBucket;
}

export const trackCtaClick = (
  ctaId: EventPayloads['cta_clicked']['cta_id'],
  ctaKind: EventPayloads['cta_clicked']['cta_kind'],
  destination: EventPayloads['cta_clicked']['destination']
) => trackEvent('cta_clicked', { cta_id: ctaId, cta_kind: ctaKind, destination });

export function trackDelegatedConversionClick(event: MouseEvent, sourcePath: string): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest<HTMLAnchorElement>('a[href]');
  if (!anchor || anchor.hasAttribute('download')) return;

  let url: URL;
  try {
    url = new URL(anchor.getAttribute('href') ?? '', window.location.origin);
  } catch {
    return;
  }
  if (url.origin !== window.location.origin) return;

  const destinationPath = normalisePath(url.pathname);
  if (!CTA_DESTINATIONS.has(destinationPath as CtaDestination)) return;
  const destination = destinationPath as CtaDestination;
  const sourceType = deriveSourceContext(sourcePath).source_page_type;

  if (destination === '/dashboard') {
    trackCtaClick('authenticated_dashboard', 'primary', destination);
  } else if (sourceType === 'council') {
    trackCtaClick('council_conversion', 'council', destination);
  } else if (sourceType === 'guide') {
    trackCtaClick('guide_conversion', 'guide', destination);
  } else if (destination === '/check') {
    trackCtaClick('eligibility_check', 'primary', destination);
  } else {
    trackCtaClick('primary_conversion', 'primary', destination);
  }
}
export const trackWizardStarted = () => trackEvent('claim_wizard_started', {});
export const trackWizardStepCompleted = (step: WizardStep) =>
  trackEvent('claim_wizard_step_completed', { step });
export function setGuestCheckFunnelVariant(): void {
  getJourneyContext('/check');
}
export const trackEligibilityStarted = () => trackEvent('eligibility_started', {});
export const trackEligibilityStepCompleted = (step: EligibilityStep) =>
  trackEvent('eligibility_step_completed', { step });
export const trackEligibilityResultViewed = (
  input: {
    result: EligibilityResult;
    authoritySlug?: string;
    ratingBand?: RatingBand;
  }
) => trackEvent('eligibility_result_viewed', {
  result: input.result,
  ...(input.authoritySlug && { authority_slug: input.authoritySlug }),
  ...(input.ratingBand && { rating_band: input.ratingBand }),
});
export const trackAccountSaveRequested = () =>
  trackEvent('account_save_requested', {});
export const trackSignUp = () => trackEvent('sign_up', { method: 'email' });
export const trackLogin = () => trackEvent('login', { method: 'email' });
export const trackClaimCreated = () => trackEvent('claim_created', {});
export const trackEvidenceUploaded = () =>
  trackEvent('evidence_uploaded', { evidence_type: 'auto' });
export const trackPackValuePreviewViewed = (score: number, dedupeScope?: string) => {
  const score_band =
    score >= 90 ? '90_100' : score >= 70 ? '70_89' : score >= 40 ? '40_69' : '0_39';
  const scope = dedupeScope ?? normalisePath(window.location.pathname);
  return trackDeduplicated(
    `pack_value_preview_viewed:${localScopeBucket(scope)}`,
    'pack_value_preview_viewed',
    { score_band }
  );
};
export const trackBeginCheckout = (packType: PackType, value: number) =>
  trackEvent('begin_checkout', { pack_type: packType, value });
export function trackPurchase(
  packType: PackType,
  value: number,
  transactionId?: string
): boolean {
  if (!transactionId || !/^pi_[a-zA-Z0-9]+$/.test(transactionId)) return false;
  return trackDeduplicated(`purchase:${localScopeBucket(transactionId)}`, 'purchase', {
    pack_type: packType,
    value,
    transaction_id: transactionId,
  });
}
export const trackPaymentFailure = (packType: PackType, errorCode: PaymentErrorCode) =>
  trackEvent('payment_failed', { pack_type: packType, error_code: errorCode });
export const trackPackGenerated = (packType: PackType, dedupeScope: string = packType) => {
  return trackDeduplicated(
    `pack_generated:${packType}:${localScopeBucket(dedupeScope)}`,
    'pack_generated',
    { pack_type: packType }
  );
};
export const trackPackViewed = (packType: PackType, dedupeScope: string = packType) => {
  return trackDeduplicated(
    `pack_viewed:${packType}:${localScopeBucket(dedupeScope)}`,
    'pack_viewed',
    { pack_type: packType }
  );
};
